import { useEffect, useMemo, useRef, useState } from "react";
import {
  defaultCountries,
  CountryIso2,
  CountryData,
  FlagImage,
} from "react-international-phone";
import "react-international-phone/style.css";
import { isValidPhoneNumber } from "libphonenumber-js";
import { ChevronDown, Search } from "lucide-react";

type AuthPhoneInputProps = {
  name: string;
  label: string;
  value?: string;
  onChange: (value: string) => void;
  error?: string;
  defaultCountry?: CountryIso2;
  countries?: CountryData[];
  placeholder?: string;
  onValidityChange?: (isValid: boolean) => void;
};

const normalizeValue = (v?: string) => (v || "").replace(/\s+/g, "");

const findCountryByIso2 = (
  countries: CountryData[],
  iso2: CountryIso2
): CountryData => {
  return countries.find((c) => c[1] === iso2) || countries[0];
};

const findCountryFromPhone = (
  phone: string,
  countries: CountryData[]
): CountryData | null => {
  if (!phone.startsWith("+")) return null;

  const sorted = [...countries].sort(
    (a, b) => String(b[2]).length - String(a[2]).length
  );

  for (const c of sorted) {
    const dial = String(c[2]);
    if (phone.startsWith(`+${dial}`)) return c;
  }

  return null;
};

const getLocalFromFull = (full: string, dialCode: string) => {
  const normalized = normalizeValue(full);
  const prefix = `+${dialCode}`;
  if (!normalized.startsWith(prefix)) return "";
  return normalized.slice(prefix.length);
};

const AuthPhoneInput = ({
  name,
  label,
  value = "",
  onChange,
  error,
  defaultCountry = "in",
  countries = defaultCountries,
  placeholder = "Enter your phone number",
  onValidityChange,
}: AuthPhoneInputProps) => {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const lastEmittedRef = useRef("");
  const didInitRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const initialCountry = findCountryByIso2(countries, defaultCountry);
  const [selectedCountry, setSelectedCountry] =
    useState<CountryData>(initialCountry);
  const [localNumber, setLocalNumber] = useState("");

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };

    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const filteredCountries = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return countries;

    return countries.filter((c) => {
      const name = String(c[0] ?? "").toLowerCase();
      const iso2 = String(c[1] ?? "").toLowerCase();
      const dial = String(c[2] ?? "");
      const qq = query.replace("+", "");

      return (
        name.includes(query) || iso2.includes(query) || dial.includes(qq)
      );
    });
  }, [q, countries]);

  const countryName = String(selectedCountry[0]);
  const countryIso2 = selectedCountry[1] as CountryIso2;
  const countryDialCode = String(selectedCountry[2]);

  const emitValue = (dialCode: string, rawLocal: string) => {
    const digitsOnly = rawLocal.replace(/\D/g, "");
    const full = digitsOnly ? `+${dialCode}${digitsOnly}` : "";

    lastEmittedRef.current = full;
    onChange(full);

    if (onValidityChange) {
      onValidityChange(full ? isValidPhoneNumber(full) : false);
    }
  };

  // Initialize from incoming value once, and only resync on real external changes
  useEffect(() => {
    const incoming = normalizeValue(value);

    // first mount / prefilled value
    if (!didInitRef.current) {
      didInitRef.current = true;

      if (!incoming) {
        setSelectedCountry(findCountryByIso2(countries, defaultCountry));
        setLocalNumber("");
        return;
      }

      const matched = findCountryFromPhone(incoming, countries);
      if (matched) {
        setSelectedCountry(matched);
        setLocalNumber(getLocalFromFull(incoming, String(matched[2])));
      }

      return;
    }

    // ignore redux echo from our own typing
    if (incoming === lastEmittedRef.current) return;

    // allow external reset
    if (!incoming) {
      setLocalNumber("");
      setSelectedCountry(findCountryByIso2(countries, defaultCountry));
      return;
    }

    // allow external prefill/edit
    const matched = findCountryFromPhone(incoming, countries);
    if (matched) {
      setSelectedCountry(matched);
      setLocalNumber(getLocalFromFull(incoming, String(matched[2])));
    }
  }, [value, countries, defaultCountry]);

  return (
    <div className="space-y-2" ref={wrapRef}>
      <label className="text-[#121217] text-sm tracking-normal font-medium">
        {label}
      </label>

      <div className="flex items-center gap-x-2 relative">
        <button
          type="button"
          onClick={() => setOpen((s) => !s)}
          className={`
            auth-input-shadow
            px-2
            py-[11px]
            rounded-lg
            flex
            items-center
            gap-2
            select-none
            ${error ? "border border-red-500" : ""}
          `}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <FlagImage
            iso2={countryIso2}
            className="w-[36px] h-[19px] rounded"
          />
          <p className="text-[#121217] text-sm tracking-normal font-medium">
            +{countryDialCode}
          </p>
          <ChevronDown color="#98A2B3" size={15} />
        </button>

        <input
          id={name}
          name={name}
          type="tel"
          inputMode="numeric"
          value={localNumber}
          onChange={(e) => {
            const raw = e.target.value;
            const nextLocal = raw.replace(/[^\d]/g, "");
            setLocalNumber(nextLocal);
            emitValue(countryDialCode, nextLocal);
          }}
          placeholder={placeholder}
          autoComplete="tel-national"
          className={`
            py-[12.5px]
            px-4
            text-sm
            rounded-lg
            w-full
            outline-none
            bg-white
            appearance-none
            focus:outline-none
            focus:ring-2
            focus:ring-[#FF6600]
            transition-all
            duration-200
            ${
              error
                ? "border border-red-500 focus:ring-red-500"
                : "border border-[#E4E7EC]"
            }
          `}
          style={{
            boxShadow:
              "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
            WebkitBoxShadow:
              "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
            backgroundClip: "padding-box",
            WebkitAppearance: "none",
          }}
        />

        {open && (
          <div
            className="absolute z-50 top-[52px] left-0 w-[320px] bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
            role="listbox"
          >
            <div className="p-2 border-b border-gray-100">
              <div className="flex items-center gap-2 px-2 py-2 rounded-lg bg-gray-50">
                <Search size={16} className="text-gray-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search country or code"
                  className="w-full bg-transparent outline-none text-sm text-gray-700"
                  autoFocus
                />
              </div>
            </div>

            <div className="max-h-[280px] overflow-y-auto">
              {filteredCountries.map((c) => {
                const cname = String(c[0]);
                const iso2 = c[1] as CountryIso2;
                const dial = String(c[2]);
                const active = iso2 === countryIso2;

                return (
                  <button
                    key={`${iso2}-${dial}-${cname}`}
                    type="button"
                    onClick={() => {
                      setSelectedCountry(c);
                      setOpen(false);
                      setQ("");
                      emitValue(dial, localNumber);
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50 ${
                      active ? "bg-gray-50" : ""
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <FlagImage
                        iso2={iso2}
                        className="w-[28px] h-[16px] rounded"
                      />
                      <div className="flex flex-col">
                        <span className="text-sm text-[#121217] font-medium leading-5">
                          {cname}
                        </span>
                        <span className="text-xs text-gray-500">
                          {iso2.toUpperCase()}
                        </span>
                      </div>
                    </div>

                    <span className="text-sm text-gray-700 font-medium">
                      +{dial}
                    </span>
                  </button>
                );
              })}

              {filteredCountries.length === 0 && (
                <div className="p-3 text-sm text-gray-500">
                  No countries found.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};

export default AuthPhoneInput;